import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Add, ModeEditOutlineTwoTone } from "@mui/icons-material";
import { Box, Button, Chip, IconButton } from "@mui/material";

import AppTable from "components/Shared/AppTable";
import FormDrawer from "components/Shared/FormDrawer";
import { useVendorProfileDetailContext } from "contexts/VendorProfileDetailContext";
import {
    useAddVendorBankDetailsMutation,
    useCreateVendorBankDetailsMutation,
    useUpdateVendorBankDetailsMutation,
} from "store/services/vendorProfileApi";
import VendorProfileBankDetailsForm from "../Components/VendorProfileBankDetailsForm";

const VendorBankDetailsTab = ({ vendorId }) => {
    const [showBankDetailsForm, setShowBankDetailsForm] = useState(false);
    const [selectedBank, setSelectedBank] = useState(null);

    const { vendorProfileDetail, loadingvendorProfileDetail } =
        useVendorProfileDetailContext();

    const allBankAccounts = useMemo(() => {
        const list = [];
        const seen = new Set();

        if (Array.isArray(vendorProfileDetail?.bankAccount)) {
            vendorProfileDetail.bankAccount.forEach((item) => {
                const accNo = item?.bankAccountNumber != null ? String(item.bankAccountNumber) : "";
                if (item?.bankName && !seen.has(accNo)) {
                    seen.add(accNo);
                    list.push(item);
                }
            });
        }

        if (vendorProfileDetail?.vendorProfileBankDetail?.bankName) {
            const vDetail = vendorProfileDetail.vendorProfileBankDetail;
            const accNo = vDetail?.bankAccountNumber != null ? String(vDetail.bankAccountNumber) : "";
            if (!seen.has(accNo)) {
                seen.add(accNo);
                list.push({
                    ...vDetail,
                    status: "Active",
                });
            }
        }

        return list;
    }, [vendorProfileDetail]);

    const columns = [
        {
            name: "Bank Name",
            selector: (row) => row?.bankName,
            cell: (row) => <div>{row?.bankName || "-"}</div>,
        },
        {
            name: "Account Name",
            selector: (row) => row?.bankAccountName,
            cell: (row) => <div>{row?.bankAccountName || "-"}</div>,
        },
        {
            name: "Account Number",
            selector: (row) => row?.bankAccountNumber,
            cell: (row) => <div>{row?.bankAccountNumber || "-"}</div>,
        },
        {
            name: "Status",
            selector: (row) => row?.status,
            cell: (row) => (
                <Chip
                    label={row?.status || "Active"}
                    color={row?.status === "Inactive" ? "default" : "success"}
                    size="small"
                    variant="outlined"
                />
            ),
        },
        {
            name: "Actions",
            cell: (row) => (
                <IconButton
                    size="small"
                    aria-label="Edit Bank Details"
                    onClick={() => {
                        setSelectedBank(row);
                        setShowBankDetailsForm(true);
                    }}
                >
                    <ModeEditOutlineTwoTone />
                </IconButton>
            ),
            right: true,
        },
    ];

    const [addBankDetails, { isLoading: addingBankDetails }] =
        useAddVendorBankDetailsMutation();
    const [createBankDetails, { isLoading: creatingBankDetails }] =
        useCreateVendorBankDetailsMutation();
    const [updateBankDetails, { isLoading: updatingBankDetails }] =
        useUpdateVendorBankDetailsMutation();

    const handleCancel = () => {
        setSelectedBank(null);
        setShowBankDetailsForm(false);
    };

    const handleSubmit = async (data) => {
        try {
            if (selectedBank?._id) {
                await updateBankDetails({
                    vendorId,
                    _id: selectedBank._id,
                    bankId: selectedBank._id,
                    status: selectedBank.status || "Active",
                    ...data,
                }).unwrap();
            } else {
                await createBankDetails({
                    vendorId,
                    status: "Active",
                    ...data,
                }).unwrap();
            }
            await addBankDetails({
                vendorId,
                ...data,
            }).unwrap();

            toast.success("Bank details saved successfully!");
            handleCancel();
        } catch (err) {
            toast.error(err?.data?.message ?? "Couldn't save bank details!");
        }
    };

    const isBusy = addingBankDetails || creatingBankDetails || updatingBankDetails;

    return (
        <>
            <Box display="flex" justifyContent="end" padding={3}>
                <Button
                    startIcon={<Add />}
                    variant="outlined"
                    onClick={() => {
                        setSelectedBank(null);
                        setShowBankDetailsForm(true);
                    }}
                >
                    Add Bank Details
                </Button>
            </Box>

            <AppTable
                columns={columns}
                data={allBankAccounts}
                progressPending={loadingvendorProfileDetail}
                pagination={false}
            />

            <FormDrawer open={showBankDetailsForm} onClose={handleCancel}>
                <VendorProfileBankDetailsForm
                    isBusy={isBusy}
                    onSubmit={handleSubmit}
                    bankDetails={selectedBank}
                    vendorId={vendorId}
                    onCancel={handleCancel}
                />
            </FormDrawer>
        </>
    );
};

export default VendorBankDetailsTab;